import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BomberosHeader } from '../../components/BomberosHeader';
import {
    bomberosRolesAPI,
    bomberosUsuariosRolesAPI,
    bomberosCompaniaAPI,
    authAPI,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Settings,
    Shield,
    Users,
    Plus,
    Pencil,
    Trash2,
    Save,
    RefreshCw,
    CheckCircle,
    XCircle,
    UserPlus,
    Flame,
    Building2,
    History,
} from 'lucide-react';

// Categorías de permisos para mejor organización visual
const PERMISSION_CATEGORIES = {
    'Dashboard': ['ver_dashboard', 'ver_kpis'],
    'Personal': ['ver_personal', 'crear_personal', 'editar_personal', 'eliminar_personal'],
    'Material Mayor': ['ver_material_mayor', 'crear_material_mayor', 'editar_material_mayor', 'eliminar_material_mayor', 'ver_checklists', 'crear_checklists'],
    'EPP': ['ver_epp', 'crear_epp', 'editar_epp', 'eliminar_epp', 'asignar_epp', 'solicitar_baja_epp'],
    'Capacitaciones': ['ver_capacitaciones', 'crear_capacitaciones', 'editar_capacitaciones', 'eliminar_capacitaciones', 'registrar_asistencia', 'registrar_evaluaciones'],
    'Alertas': ['ver_alertas', 'resolver_alertas', 'generar_alertas'],
    'Reportes': ['ver_reportes', 'exportar_reportes'],
    'Compañías': ['ver_companias', 'crear_companias', 'editar_companias', 'eliminar_companias'],
    'Validación': ['validar_documentos'],
    'Configuración': ['gestionar_roles', 'gestionar_usuarios'],
};

export default function BomberosConfiguracionPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [usuariosRoles, setUsuariosRoles] = useState([]);
    const [permisosDisponibles, setPermisosDisponibles] = useState([]);
    const [companias, setCompanias] = useState([]);
    
    // Dialog states
    const [showRolDialog, setShowRolDialog] = useState(false);
    const [showAsignarDialog, setShowAsignarDialog] = useState(false);
    const [showCompaniasDialog, setShowCompaniasDialog] = useState(false);
    const [editingRol, setEditingRol] = useState(null);
    const [editingUsuario, setEditingUsuario] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // Form states
    const [formRol, setFormRol] = useState({
        codigo: '',
        nombre: '',
        descripcion: '',
        permisos: [],
    });
    const [asignarForm, setAsignarForm] = useState({
        usuario_id: '',
        rol_bomberos_id: '',
    });
    const [companiasForm, setCompaniasForm] = useState({
        todas: true,
        companias_ids: [],
    });
    
    // Estados para gestión de usuarios
    const [showCrearUsuarioDialog, setShowCrearUsuarioDialog] = useState(false);
    const [showEditarUsuarioDialog, setShowEditarUsuarioDialog] = useState(false);
    const [nuevoUsuario, setNuevoUsuario] = useState({ 
        email: '', 
        nombre: '', 
        password: '', 
        rol: 'user',
        rol_bomberos_id: '',
        companias_asignadas: [],
        todas_companias: true,
    });
    const [usuarioEditar, setUsuarioEditar] = useState(null);
    const [savingUsuario, setSavingUsuario] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [rolesRes, permisosRes, usuariosRes, usuariosRolesRes, companiasRes] = await Promise.all([
                bomberosRolesAPI.getAll(),
                bomberosRolesAPI.getPermisosDisponibles(),
                authAPI.getUsuarios(),
                bomberosUsuariosRolesAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
            ]);
            setRoles(rolesRes.data);
            setPermisosDisponibles(permisosRes.data.permisos);
            setUsuarios(usuariosRes.data);
            setUsuariosRoles(usuariosRolesRes.data);
            setCompanias(companiasRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleInicializarRoles = async () => {
        try {
            setSaving(true);
            const res = await bomberosRolesAPI.inicializar();
            toast.success(res.data.message);
            loadData();
        } catch (err) {
            toast.error('Error al inicializar roles');
        } finally {
            setSaving(false);
        }
    };

    const handleCrearUsuario = async () => {
        if (!nuevoUsuario.email || !nuevoUsuario.nombre || !nuevoUsuario.password) {
            toast.error('Complete todos los campos requeridos');
            return;
        }
        
        try {
            setSavingUsuario(true);
            
            // Crear usuario con compañías asignadas
            const userData = {
                email: nuevoUsuario.email,
                nombre: nuevoUsuario.nombre,
                password: nuevoUsuario.password,
                rol: nuevoUsuario.rol,
                companias_asignadas: nuevoUsuario.todas_companias ? [] : nuevoUsuario.companias_asignadas,
            };
            
            const response = await authAPI.createUsuario(userData);
            const userId = response.data.id;
            
            // Si se seleccionó un rol de bomberos, asignarlo
            if (nuevoUsuario.rol_bomberos_id && userId) {
                try {
                    await bomberosUsuariosRolesAPI.asignar({
                        usuario_id: userId,
                        rol_id: nuevoUsuario.rol_bomberos_id,
                    });
                } catch (err) {
                    console.error('Error asignando rol de bomberos:', err);
                    // No fallar si no se pudo asignar el rol
                }
            }
            
            toast.success('Usuario creado correctamente');
            setShowCrearUsuarioDialog(false);
            setNuevoUsuario({ 
                email: '', 
                nombre: '', 
                password: '', 
                rol: 'user',
                rol_bomberos_id: '',
                companias_asignadas: [],
                todas_companias: true,
            });
            loadData();
        } catch (err) {
            console.error('Error creating user:', err);
            toast.error(err.response?.data?.detail || 'Error al crear usuario');
        } finally {
            setSavingUsuario(false);
        }
    };

    const handleEditarUsuario = async () => {
        if (!usuarioEditar) return;
        
        try {
            setSavingUsuario(true);
            const dataToUpdate = {
                nombre: usuarioEditar.nombre,
                rol: usuarioEditar.rol,
                companias_asignadas: usuarioEditar.companias_asignadas || [],
            };
            // Solo incluir password si se cambió
            if (usuarioEditar.newPassword) {
                dataToUpdate.password = usuarioEditar.newPassword;
            }
            await authAPI.updateUsuario(usuarioEditar.id, dataToUpdate);
            
            // Manejar rol de bomberos
            const usuarioRolActual = usuariosRoles.find(ur => ur.usuario_id === usuarioEditar.id);
            const nuevoRolBomberos = usuarioEditar.rol_bomberos_id;
            
            if (nuevoRolBomberos && nuevoRolBomberos !== 'none') {
                // Asignar o actualizar rol de bomberos
                try {
                    await bomberosUsuariosRolesAPI.asignar({
                        usuario_id: usuarioEditar.id,
                        rol_id: nuevoRolBomberos,
                    });
                } catch (err) {
                    console.error('Error asignando rol de bomberos:', err);
                }
            } else if (usuarioRolActual && (!nuevoRolBomberos || nuevoRolBomberos === 'none')) {
                // Remover rol de bomberos si tenía uno y ahora se quita
                try {
                    await bomberosUsuariosRolesAPI.remover(usuarioEditar.id);
                } catch (err) {
                    console.error('Error removiendo rol de bomberos:', err);
                }
            }
            
            toast.success('Usuario actualizado correctamente');
            setShowEditarUsuarioDialog(false);
            setUsuarioEditar(null);
            loadData();
        } catch (err) {
            console.error('Error updating user:', err);
            toast.error(err.response?.data?.detail || 'Error al actualizar usuario');
        } finally {
            setSavingUsuario(false);
        }
    };

    const handleOpenRolDialog = (rol = null) => {
        if (rol) {
            setEditingRol(rol);
            setFormRol({
                codigo: rol.codigo,
                nombre: rol.nombre,
                descripcion: rol.descripcion || '',
                permisos: rol.permisos || [],
            });
        } else {
            setEditingRol(null);
            setFormRol({
                codigo: '',
                nombre: '',
                descripcion: '',
                permisos: [],
            });
        }
        setShowRolDialog(true);
    };

    const handleSaveRol = async () => {
        if (!formRol.codigo || !formRol.nombre) {
            toast.error('Código y nombre son requeridos');
            return;
        }
        
        try {
            setSaving(true);
            if (editingRol) {
                await bomberosRolesAPI.update(editingRol.id, formRol);
                toast.success('Rol actualizado');
            } else {
                await bomberosRolesAPI.create(formRol);
                toast.success('Rol creado');
            }
            setShowRolDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar rol');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRol = async (rolId) => {
        if (!window.confirm('¿Está seguro de eliminar este rol?')) return;
        
        try {
            await bomberosRolesAPI.delete(rolId);
            toast.success('Rol eliminado');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar rol');
        }
    };

    const handleTogglePermiso = (permiso) => {
        setFormRol(prev => {
            const permisos = prev.permisos.includes(permiso)
                ? prev.permisos.filter(p => p !== permiso)
                : [...prev.permisos, permiso];
            return { ...prev, permisos };
        });
    };

    const handleToggleCategoryPermissions = (category, permisos) => {
        const allSelected = permisos.every(p => formRol.permisos.includes(p));
        setFormRol(prev => {
            let newPermisos;
            if (allSelected) {
                newPermisos = prev.permisos.filter(p => !permisos.includes(p));
            } else {
                newPermisos = [...new Set([...prev.permisos, ...permisos])];
            }
            return { ...prev, permisos: newPermisos };
        });
    };

    const handleAsignarRol = async () => {
        if (!asignarForm.usuario_id || !asignarForm.rol_bomberos_id) {
            toast.error('Seleccione usuario y rol');
            return;
        }
        
        try {
            setSaving(true);
            await bomberosUsuariosRolesAPI.asignar(asignarForm);
            toast.success('Rol asignado correctamente');
            setShowAsignarDialog(false);
            setAsignarForm({ usuario_id: '', rol_bomberos_id: '' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al asignar rol');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoverRol = async (usuarioId) => {
        if (!window.confirm('¿Está seguro de remover el rol de este usuario?')) return;
        
        try {
            await bomberosUsuariosRolesAPI.remover(usuarioId);
            toast.success('Rol removido');
            loadData();
        } catch (err) {
            toast.error('Error al remover rol');
        }
    };
    
    const handleOpenCompaniasDialog = (usuarioRol) => {
        // Buscar el usuario completo para obtener companias_asignadas
        const usuario = usuarios.find(u => u.id === usuarioRol.usuario_id);
        const companiasAsignadas = usuario?.companias_asignadas || [];
        
        setEditingUsuario(usuarioRol);
        setCompaniasForm({
            todas: companiasAsignadas.length === 0,
            companias_ids: companiasAsignadas,
        });
        setShowCompaniasDialog(true);
    };
    
    const handleToggleCompania = (companiaId) => {
        setCompaniasForm(prev => {
            const newIds = prev.companias_ids.includes(companiaId)
                ? prev.companias_ids.filter(id => id !== companiaId)
                : [...prev.companias_ids, companiaId];
            return { ...prev, companias_ids: newIds, todas: false };
        });
    };
    
    const handleSetTodasCompanias = (checked) => {
        setCompaniasForm({
            todas: checked,
            companias_ids: checked ? [] : companiasForm.companias_ids,
        });
    };
    
    const handleSaveCompanias = async () => {
        try {
            setSaving(true);
            const companias_asignadas = companiasForm.todas ? [] : companiasForm.companias_ids;
            
            await authAPI.updateUsuario(editingUsuario.usuario_id, { companias_asignadas });
            toast.success('Acceso a compañías actualizado');
            setShowCompaniasDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al actualizar compañías');
        } finally {
            setSaving(false);
        }
    };
    
    const getCompaniasLabel = (usuarioId) => {
        const usuario = usuarios.find(u => u.id === usuarioId);
        const asignadas = usuario?.companias_asignadas || [];
        if (asignadas.length === 0) return 'Todas';
        if (asignadas.length === 1) {
            const cia = companias.find(c => c.id === asignadas[0]);
            return cia?.nombre || '1 compañía';
        }
        return `${asignadas.length} compañías`;
    };

    const getPermisoDescripcion = (codigo) => {
        const permiso = permisosDisponibles.find(p => p.codigo === codigo);
        return permiso?.descripcion || codigo;
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid gap-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Settings className="h-6 w-6 text-orange-500" />
                            Configuración del Módulo
                        </h1>
                        <p className="text-muted-foreground">Gestión de roles y permisos</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="roles" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="tab-roles">
                        <Shield className="h-4 w-4" />
                        Roles
                    </TabsTrigger>
                    <TabsTrigger value="gestion-usuarios" className="flex items-center gap-2" data-testid="tab-gestion-usuarios">
                        <UserPlus className="h-4 w-4" />
                        Gestión Usuarios
                    </TabsTrigger>
                    <TabsTrigger value="usuarios" className="flex items-center gap-2" data-testid="tab-usuarios">
                        <Users className="h-4 w-4" />
                        Asignación Compañías
                    </TabsTrigger>
                    <TabsTrigger value="auditoria" className="flex items-center gap-2" data-testid="tab-auditoria">
                        <History className="h-4 w-4" />
                        Auditoría
                    </TabsTrigger>
                </TabsList>

                {/* Tab: Roles */}
                <TabsContent value="roles" className="space-y-4">
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Roles del Sistema</CardTitle>
                                    <CardDescription>Define los roles y sus permisos para el módulo de bomberos</CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    {roles.length === 0 && (
                                        <Button variant="outline" onClick={handleInicializarRoles} disabled={saving} data-testid="init-roles-btn">
                                            <Flame className="h-4 w-4 mr-2" />
                                            Crear Roles Predefinidos
                                        </Button>
                                    )}
                                    <Button onClick={() => handleOpenRolDialog()} data-testid="new-rol-btn">
                                        <Plus className="h-4 w-4 mr-2" />
                                        Nuevo Rol
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {roles.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>No hay roles configurados</p>
                                    <p className="text-sm">Crea roles predefinidos o agrega uno nuevo</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {roles.map((rol) => (
                                        <Card key={rol.id} className="border" data-testid={`rol-${rol.codigo}`}>
                                            <CardContent className="pt-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="font-semibold text-lg">{rol.nombre}</h3>
                                                            <Badge variant="outline">{rol.codigo}</Badge>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">{rol.descripcion}</p>
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {rol.permisos.slice(0, 5).map((p) => (
                                                                <Badge key={p} variant="secondary" className="text-xs">
                                                                    {getPermisoDescripcion(p)}
                                                                </Badge>
                                                            ))}
                                                            {rol.permisos.length > 5 && (
                                                                <Badge variant="secondary" className="text-xs">
                                                                    +{rol.permisos.length - 5} más
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleOpenRolDialog(rol)}
                                                            data-testid={`edit-rol-${rol.codigo}`}
                                                        >
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDeleteRol(rol.id)}
                                                            className="text-red-500 hover:text-red-600"
                                                            data-testid={`delete-rol-${rol.codigo}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Tab: Gestión de Usuarios */}
                <TabsContent value="gestion-usuarios" className="space-y-4">
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <UserPlus className="h-5 w-5" />
                                        Gestión de Usuarios
                                    </CardTitle>
                                    <CardDescription>
                                        Crear y administrar usuarios del sistema
                                    </CardDescription>
                                </div>
                                <Button onClick={() => {
                                    setShowCrearUsuarioDialog(true);
                                    setNuevoUsuario({ 
                                        email: '', 
                                        nombre: '', 
                                        password: '', 
                                        rol: 'user',
                                        rol_bomberos_id: '',
                                        companias_asignadas: [],
                                        todas_companias: true,
                                    });
                                }} data-testid="crear-usuario-btn">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Crear Usuario
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="space-y-2">
                                    {[...Array(3)].map((_, i) => (
                                        <Skeleton key={i} className="h-12 w-full" />
                                    ))}
                                </div>
                            ) : usuarios.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    No hay usuarios registrados
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nombre</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Rol</TableHead>
                                            <TableHead>Compañías</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {usuarios.map((u) => (
                                            <TableRow key={u.id}>
                                                <TableCell className="font-medium">{u.nombre}</TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>
                                                    <Badge variant={u.rol === 'admin' ? 'default' : 'secondary'}>
                                                        {u.rol}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {u.companias_asignadas?.length > 0 ? (
                                                        <span className="text-sm">
                                                            {u.companias_asignadas.length} asignada(s)
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">
                                                            Todas
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            // Buscar el rol de bomberos actual del usuario
                                                            const usuarioRol = usuariosRoles.find(ur => ur.usuario_id === u.id);
                                                            setUsuarioEditar({
                                                                ...u,
                                                                rol_bomberos_id: usuarioRol?.rol_id || '',
                                                            });
                                                            setShowEditarUsuarioDialog(true);
                                                        }}
                                                        data-testid={`editar-usuario-${u.id}`}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Tab: Usuarios */}
                <TabsContent value="usuarios" className="space-y-4">
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Usuarios con Roles Asignados</CardTitle>
                                    <CardDescription>Gestiona qué usuarios tienen acceso al módulo de bomberos</CardDescription>
                                </div>
                                <Button onClick={() => setShowAsignarDialog(true)} data-testid="asignar-rol-btn">
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Asignar Rol
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {usuariosRoles.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                    <p>No hay usuarios con roles asignados</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Usuario</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Rol</TableHead>
                                            <TableHead>Compañías</TableHead>
                                            <TableHead className="w-[120px]">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {usuariosRoles.map((ur) => (
                                            <TableRow key={ur.id} data-testid={`usuario-rol-${ur.usuario_id}`}>
                                                <TableCell className="font-medium">{ur.usuario_nombre}</TableCell>
                                                <TableCell>{ur.usuario_email}</TableCell>
                                                <TableCell>
                                                    <Badge>{ur.rol_nombre}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Building2 className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm">{getCompaniasLabel(ur.usuario_id)}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleOpenCompaniasDialog(ur)}
                                                            title="Configurar acceso a compañías"
                                                            data-testid={`editar-companias-${ur.usuario_id}`}
                                                        >
                                                            <Building2 className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleRemoverRol(ur.usuario_id)}
                                                            className="text-red-500 hover:text-red-600"
                                                            data-testid={`remover-rol-${ur.usuario_id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Tab: Auditoría */}
                <TabsContent value="auditoria" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <History className="h-5 w-5 text-primary" />
                                Registro de Actividades
                            </CardTitle>
                            <CardDescription>
                                Accede al historial completo de cambios y modificaciones del sistema
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col items-center justify-center py-8 space-y-4">
                                <div className="bg-primary/10 p-4 rounded-full">
                                    <History className="h-12 w-12 text-primary" />
                                </div>
                                <div className="text-center space-y-2">
                                    <h3 className="text-lg font-semibold">Sistema de Auditoría</h3>
                                    <p className="text-muted-foreground max-w-md">
                                        Visualiza todos los cambios realizados en el sistema, incluyendo creación,
                                        edición y eliminación de registros. También puedes revertir cambios si es necesario.
                                    </p>
                                </div>
                                <Button 
                                    onClick={() => navigate('/bomberos/registro-actividades')}
                                    className="mt-4"
                                    data-testid="go-to-audit-btn"
                                >
                                    <History className="h-4 w-4 mr-2" />
                                    Ver Registro de Actividades
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Dialog: Crear/Editar Rol */}
            <Dialog open={showRolDialog} onOpenChange={setShowRolDialog}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingRol ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle>
                        <DialogDescription>
                            Define el nombre y los permisos del rol
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="codigo">Código *</Label>
                                <Input
                                    id="codigo"
                                    value={formRol.codigo}
                                    onChange={(e) => setFormRol(prev => ({ ...prev, codigo: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                                    placeholder="ej: inspector"
                                    disabled={!!editingRol}
                                    data-testid="input-codigo"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="nombre">Nombre *</Label>
                                <Input
                                    id="nombre"
                                    value={formRol.nombre}
                                    onChange={(e) => setFormRol(prev => ({ ...prev, nombre: e.target.value }))}
                                    placeholder="ej: Inspector de Equipos"
                                    data-testid="input-nombre"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="descripcion">Descripción</Label>
                            <Input
                                id="descripcion"
                                value={formRol.descripcion}
                                onChange={(e) => setFormRol(prev => ({ ...prev, descripcion: e.target.value }))}
                                placeholder="Describe el propósito de este rol"
                                data-testid="input-descripcion"
                            />
                        </div>

                        <div className="space-y-4">
                            <Label>Permisos ({formRol.permisos.length} seleccionados)</Label>
                            
                            <div className="grid gap-4">
                                {Object.entries(PERMISSION_CATEGORIES).map(([category, permisos]) => (
                                    <Card key={category} className="border">
                                        <CardHeader className="py-3 px-4">
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="text-base">{category}</CardTitle>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleToggleCategoryPermissions(category, permisos)}
                                                    data-testid={`toggle-category-${category}`}
                                                >
                                                    {permisos.every(p => formRol.permisos.includes(p)) ? (
                                                        <XCircle className="h-4 w-4 mr-1" />
                                                    ) : (
                                                        <CheckCircle className="h-4 w-4 mr-1" />
                                                    )}
                                                    {permisos.every(p => formRol.permisos.includes(p)) ? 'Deseleccionar' : 'Seleccionar'} todos
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="py-2 px-4">
                                            <div className="grid grid-cols-2 gap-2">
                                                {permisos.map((permiso) => (
                                                    <div key={permiso} className="flex items-center space-x-2">
                                                        <Checkbox
                                                            id={permiso}
                                                            checked={formRol.permisos.includes(permiso)}
                                                            onCheckedChange={() => handleTogglePermiso(permiso)}
                                                            data-testid={`checkbox-${permiso}`}
                                                        />
                                                        <Label
                                                            htmlFor={permiso}
                                                            className="text-sm font-normal cursor-pointer"
                                                        >
                                                            {getPermisoDescripcion(permiso)}
                                                        </Label>
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRolDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSaveRol} disabled={saving} data-testid="save-rol-btn">
                            <Save className="h-4 w-4 mr-2" />
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Asignar Rol a Usuario */}
            <Dialog open={showAsignarDialog} onOpenChange={setShowAsignarDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Asignar Rol a Usuario</DialogTitle>
                        <DialogDescription>
                            Selecciona un usuario y el rol que deseas asignarle
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Usuario</Label>
                            <Select
                                value={asignarForm.usuario_id}
                                onValueChange={(value) => setAsignarForm(prev => ({ ...prev, usuario_id: value }))}
                            >
                                <SelectTrigger data-testid="select-usuario">
                                    <SelectValue placeholder="Seleccionar usuario" />
                                </SelectTrigger>
                                <SelectContent>
                                    {usuarios
                                        .filter(u => !usuariosRoles.find(ur => ur.usuario_id === u.id))
                                        .map((u) => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.nombre} ({u.email})
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Rol</Label>
                            <Select
                                value={asignarForm.rol_bomberos_id}
                                onValueChange={(value) => setAsignarForm(prev => ({ ...prev, rol_bomberos_id: value }))}
                            >
                                <SelectTrigger data-testid="select-rol">
                                    <SelectValue placeholder="Seleccionar rol" />
                                </SelectTrigger>
                                <SelectContent>
                                    {roles.map((r) => (
                                        <SelectItem key={r.id} value={r.id}>
                                            {r.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAsignarDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleAsignarRol} disabled={saving} data-testid="confirm-asignar-btn">
                            <UserPlus className="h-4 w-4 mr-2" />
                            {saving ? 'Asignando...' : 'Asignar Rol'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Configurar Compañías */}
            <Dialog open={showCompaniasDialog} onOpenChange={setShowCompaniasDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="h-5 w-5" />
                            Acceso a Compañías
                        </DialogTitle>
                        <DialogDescription>
                            {editingUsuario && (
                                <span>Configurar qué compañías puede ver <strong>{editingUsuario.usuario_nombre}</strong></span>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Opción todas las compañías */}
                        <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                            <Checkbox
                                id="todas-companias"
                                checked={companiasForm.todas}
                                onCheckedChange={handleSetTodasCompanias}
                            />
                            <div className="flex-1">
                                <Label htmlFor="todas-companias" className="font-medium cursor-pointer">
                                    Todas las Compañías
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    Usuario transversal - puede ver y gestionar datos de todas las compañías
                                </p>
                            </div>
                        </div>

                        {/* Lista de compañías específicas */}
                        {!companiasForm.todas && (
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Compañías específicas:</Label>
                                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                                    {companias.map(cia => (
                                        <div key={cia.id} className="flex items-center space-x-3 p-3 hover:bg-muted/30">
                                            <Checkbox
                                                id={`cia-${cia.id}`}
                                                checked={companiasForm.companias_ids.includes(cia.id)}
                                                onCheckedChange={() => handleToggleCompania(cia.id)}
                                            />
                                            <Label htmlFor={`cia-${cia.id}`} className="flex-1 cursor-pointer">
                                                <span className="font-medium">{cia.nombre}</span>
                                                <span className="text-xs text-muted-foreground ml-2">
                                                    ({cia.total_bomberos || 0} bomberos)
                                                </span>
                                            </Label>
                                        </div>
                                    ))}
                                </div>
                                {companiasForm.companias_ids.length === 0 && (
                                    <p className="text-sm text-amber-600">
                                        Seleccione al menos una compañía
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCompaniasDialog(false)}>
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleSaveCompanias} 
                            disabled={saving || (!companiasForm.todas && companiasForm.companias_ids.length === 0)}
                        >
                            <Save className="h-4 w-4 mr-2" />
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Crear Usuario */}
            <Dialog open={showCrearUsuarioDialog} onOpenChange={setShowCrearUsuarioDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="h-5 w-5" />
                            Crear Nuevo Usuario
                        </DialogTitle>
                        <DialogDescription>
                            Complete los datos del usuario, rol y acceso a compañías
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                        {/* Datos básicos */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="nombre">Nombre *</Label>
                                <Input
                                    id="nombre"
                                    value={nuevoUsuario.nombre}
                                    onChange={(e) => setNuevoUsuario(prev => ({ ...prev, nombre: e.target.value }))}
                                    placeholder="Nombre completo"
                                    data-testid="input-nombre-usuario"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={nuevoUsuario.email}
                                    onChange={(e) => setNuevoUsuario(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="usuario@ejemplo.com"
                                    data-testid="input-email-usuario"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">Contraseña *</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={nuevoUsuario.password}
                                    onChange={(e) => setNuevoUsuario(prev => ({ ...prev, password: e.target.value }))}
                                    placeholder="Mínimo 6 caracteres"
                                    data-testid="input-password-usuario"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="rol-sistema">Rol del Sistema</Label>
                                <Select
                                    value={nuevoUsuario.rol}
                                    onValueChange={(value) => setNuevoUsuario(prev => ({ ...prev, rol: value }))}
                                >
                                    <SelectTrigger data-testid="select-rol-usuario">
                                        <SelectValue placeholder="Seleccione rol" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="user">Usuario</SelectItem>
                                        <SelectItem value="admin">Administrador</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Rol de Bomberos */}
                        <div className="space-y-2 pt-2 border-t">
                            <Label>Rol en Bomberos</Label>
                            <Select
                                value={nuevoUsuario.rol_bomberos_id}
                                onValueChange={(value) => setNuevoUsuario(prev => ({ ...prev, rol_bomberos_id: value === 'none' ? '' : value }))}
                            >
                                <SelectTrigger data-testid="select-rol-bomberos">
                                    <SelectValue placeholder="Seleccione rol de bomberos (opcional)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sin rol asignado</SelectItem>
                                    {roles.map(rol => (
                                        <SelectItem key={rol.id} value={rol.id}>
                                            {rol.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">Define los permisos del usuario en el módulo de bomberos</p>
                        </div>

                        {/* Acceso a Compañías */}
                        <div className="space-y-3 pt-2 border-t">
                            <Label>Acceso a Compañías</Label>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="todas-companias"
                                    checked={nuevoUsuario.todas_companias}
                                    onChange={(e) => setNuevoUsuario(prev => ({ 
                                        ...prev, 
                                        todas_companias: e.target.checked,
                                        companias_asignadas: e.target.checked ? [] : prev.companias_asignadas 
                                    }))}
                                    className="h-4 w-4 rounded border-gray-300"
                                />
                                <label htmlFor="todas-companias" className="text-sm">
                                    Acceso a todas las compañías
                                </label>
                            </div>
                            
                            {!nuevoUsuario.todas_companias && (
                                <div className="space-y-2 pl-6">
                                    <p className="text-sm text-muted-foreground">Seleccione las compañías a las que tendrá acceso:</p>
                                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-md p-2">
                                        {companias.map(cia => (
                                            <div key={cia.id} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    id={`cia-${cia.id}`}
                                                    checked={(nuevoUsuario.companias_asignadas || []).includes(cia.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setNuevoUsuario(prev => ({
                                                                ...prev,
                                                                companias_asignadas: [...(prev.companias_asignadas || []), cia.id]
                                                            }));
                                                        } else {
                                                            setNuevoUsuario(prev => ({
                                                                ...prev,
                                                                companias_asignadas: (prev.companias_asignadas || []).filter(id => id !== cia.id)
                                                            }));
                                                        }
                                                    }}
                                                    className="h-4 w-4 rounded border-gray-300"
                                                />
                                                <label htmlFor={`cia-${cia.id}`} className="text-sm">
                                                    {cia.numero ? `${cia.numero}° ` : ''}{cia.nombre}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCrearUsuarioDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleCrearUsuario} disabled={savingUsuario} data-testid="guardar-usuario-btn">
                            {savingUsuario ? (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Creando...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Crear Usuario
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Editar Usuario */}
            <Dialog open={showEditarUsuarioDialog} onOpenChange={setShowEditarUsuarioDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="h-5 w-5" />
                            Editar Usuario
                        </DialogTitle>
                        <DialogDescription>
                            Modificar datos, rol y acceso a compañías
                        </DialogDescription>
                    </DialogHeader>
                    {usuarioEditar && (
                        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        value={usuarioEditar.email}
                                        disabled
                                        className="bg-muted"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-nombre">Nombre *</Label>
                                    <Input
                                        id="edit-nombre"
                                        value={usuarioEditar.nombre}
                                        onChange={(e) => setUsuarioEditar(prev => ({ ...prev, nombre: e.target.value }))}
                                        placeholder="Nombre completo"
                                        data-testid="input-editar-nombre"
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-password">Nueva Contraseña</Label>
                                    <Input
                                        id="edit-password"
                                        type="password"
                                        value={usuarioEditar.newPassword || ''}
                                        onChange={(e) => setUsuarioEditar(prev => ({ ...prev, newPassword: e.target.value }))}
                                        placeholder="Dejar vacío para mantener"
                                        data-testid="input-editar-password"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-rol">Rol del Sistema</Label>
                                    <Select
                                        value={usuarioEditar.rol}
                                        onValueChange={(value) => setUsuarioEditar(prev => ({ ...prev, rol: value }))}
                                    >
                                        <SelectTrigger data-testid="select-editar-rol">
                                            <SelectValue placeholder="Seleccione rol" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="user">Usuario</SelectItem>
                                            <SelectItem value="admin">Administrador</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Rol de Bomberos */}
                            <div className="space-y-2 pt-2 border-t">
                                <Label>Rol en Bomberos</Label>
                                <Select
                                    value={usuarioEditar.rol_bomberos_id || 'none'}
                                    onValueChange={(value) => setUsuarioEditar(prev => ({ ...prev, rol_bomberos_id: value === 'none' ? '' : value }))}
                                >
                                    <SelectTrigger data-testid="select-editar-rol-bomberos">
                                        <SelectValue placeholder="Seleccione rol de bomberos (opcional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin rol asignado</SelectItem>
                                        {roles.map(rol => (
                                            <SelectItem key={rol.id} value={rol.id}>
                                                {rol.nombre}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">Define los permisos del usuario en el módulo de bomberos</p>
                            </div>

                            {/* Acceso a Compañías */}
                            <div className="space-y-3 pt-2 border-t">
                                <Label>Acceso a Compañías</Label>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="edit-todas-companias"
                                        checked={!usuarioEditar.companias_asignadas || usuarioEditar.companias_asignadas.length === 0}
                                        onChange={(e) => setUsuarioEditar(prev => ({ 
                                            ...prev, 
                                            companias_asignadas: e.target.checked ? [] : prev.companias_asignadas || []
                                        }))}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <label htmlFor="edit-todas-companias" className="text-sm">
                                        Acceso a todas las compañías
                                    </label>
                                </div>
                                
                                {usuarioEditar.companias_asignadas && usuarioEditar.companias_asignadas.length > 0 && (
                                    <div className="space-y-2 pl-6">
                                        <p className="text-sm text-muted-foreground">Compañías asignadas:</p>
                                        <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-md p-2">
                                            {companias.map(cia => (
                                                <div key={cia.id} className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`edit-cia-${cia.id}`}
                                                        checked={usuarioEditar.companias_asignadas.includes(cia.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setUsuarioEditar(prev => ({
                                                                    ...prev,
                                                                    companias_asignadas: [...(prev.companias_asignadas || []), cia.id]
                                                                }));
                                                            } else {
                                                                setUsuarioEditar(prev => ({
                                                                    ...prev,
                                                                    companias_asignadas: (prev.companias_asignadas || []).filter(id => id !== cia.id)
                                                                }));
                                                            }
                                                        }}
                                                        className="h-4 w-4 rounded border-gray-300"
                                                    />
                                                    <label htmlFor={`edit-cia-${cia.id}`} className="text-sm">
                                                        {cia.numero ? `${cia.numero}° ` : ''}{cia.nombre}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {(!usuarioEditar.companias_asignadas || usuarioEditar.companias_asignadas.length === 0) && (
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => setUsuarioEditar(prev => ({ ...prev, companias_asignadas: [companias[0]?.id].filter(Boolean) }))}
                                    >
                                        Restringir a compañías específicas
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditarUsuarioDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleEditarUsuario} disabled={savingUsuario} data-testid="guardar-editar-usuario-btn">
                            {savingUsuario ? (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Guardar Cambios
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </>
    );
}
